import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Divider } from '@/components/Divider/Divider.component'
import { PhotoSelector } from '@/components/PhotoSelector/PhotoSelector'
import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useLazyUserInfoQuery } from '@/store/api/Users/Users.query'
import {
  selectImpersonationCreditsFromStore,
  selectUserInfo,
} from '@/store/api/Users/Users.selector'
import { setUserInfo } from '@/store/api/Users/Users.slice'
import type { RootState } from '@/store/store'

import { ConfirmInformation } from '../DigitalFootprintForm/Components/ConfirmInformation'
import { SocialMediaAccounts } from '../DigitalFootprintForm/Components/SocialMediaAccounts'

export const ImpersonationConfirmInfo = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const store = useStore()

  const impersonationCredits = useSelector(selectImpersonationCreditsFromStore)

  const [triggerGetUserInfo, { data, isLoading, isSuccess }] =
    useLazyUserInfoQuery()

  useEffect(() => {
    triggerGetUserInfo()
  }, [triggerGetUserInfo])

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setUserInfo(data))
    }
  }, [isSuccess, data, dispatch])

  const onConfirm = () => {
    const latestUserInfo = selectUserInfo(store.getState() as RootState)

    if (!latestUserInfo?.lastname || latestUserInfo.lastname.trim() === '') {
      toast.info(t('search.common.pleaseEnterLastName'))
      return
    }

    if (
      !latestUserInfo?.socialMedias ||
      latestUserInfo.socialMedias.length === 0
    ) {
      toast.info(t('search.common.pleaseAddAtLeastOneSocialMedia'))
      return
    }

    if (!latestUserInfo?.profileImgUrl) {
      toast.info(t('impersonation.confirmInfo.pleaseAddPhoto'))
      return
    }

    // With credits available we go straight to the paid full search
    // (covers post-purchase and landing-funnel users); otherwise run
    // the free screening first.
    if (impersonationCredits > 0) {
      navigate(AppRoutes.impersonationGeneratingReport)
    } else {
      navigate(AppRoutes.impersonationScreening)
    }
  }

  return (
    <WeScreen
      useNextButton={false}
      isLoading={isLoading}
      customViewStyles="max-md:p-0"
      useHeaderBackButton
      onClickBack={() => navigate(AppRoutes.dashboard)}
    >
      <div className="flex w-full h-full gap-10 items-center justify-center">
        <div className="flex flex-col p-5 gap-5 w-1/2 max-md:w-full max-lg:w-3/4 bg-baseColorHighlight rounded-2xl">
          <div className="flex flex-col gap-2">
            <p className="text-2xl font-medium text-neutralColor">
              {t('impersonation.confirmInfo.title')}
            </p>
            <p className="text-base font-normal text-neutralColor80">
              {t('impersonation.confirmInfo.subtitle')}
            </p>
          </div>
          <Divider />
          <PhotoSelector
            initialImageUrl={data?.profilePicUrl || ''}
            fullname={data?.firstname + ' ' + data?.lastname}
          />
          <Divider />
          <ConfirmInformation />
          <Divider />
          <SocialMediaAccounts />
          <Divider />

          <div className="flex flex-row gap-5 w-full justify-end">
            <Button
              disabled={isLoading}
              onClick={onConfirm}
              className="font-medium text-base h-[46px] px-5 rounded-3xl py-3"
            >
              {t('impersonation.confirmInfo.confirmButton')}
            </Button>
          </div>
        </div>
      </div>
    </WeScreen>
  )
}
